import axios from "axios";
import { API_URL } from "../constants";

export interface DepartmentMember {
  companyUserId: string;
  userId: string | null;
  name: string;
  email: string;
  position: string;
  avatarFileId: string | null;
  // Present on the hydrated detail row (get-department-row).
  isDisabled?: boolean;
  isActivated?: boolean;
}

/** Child department nested under a parent (parentDepartment set). */
export interface DepartmentSubgroup {
  id: string;
  name: string;
  description?: string | null;
  parentDepartment: string;
  head: DepartmentMember | null;
  /** The head may see this department's employees (Personalas). Optional until every row carries it. */
  headHasDataAccess?: boolean;
  members: DepartmentMember[];
  memberCount: number;
}

export interface Department {
  id: string;
  name: string;
  description: string | null;
  company: string;
  head: DepartmentMember | null;
  /**
   * «Suteikti vadovui prieigą prie darbuotojų duomenų»: the head sees this
   * department's (and its sub-departments') employees in Personalas.
   */
  headHasDataAccess?: boolean;
  parentDepartment?: string | null;
  members: DepartmentMember[];
  memberCount: number;
  /** Child departments (rows with parentDepartment = this.id). */
  subgroups?: DepartmentSubgroup[];
  createdAt?: number;
  updatedAt?: number;
}

export interface DepartmentFormData {
  id?: string;
  name: string;
  description?: string;
  head?: string | null;
  headHasDataAccess?: boolean;
  memberIds: string[];
  parentDepartment?: string | null;
}

export interface AssignableDepartmentsResponse {
  departments: { id: string; name: string }[];
  /** A hiring head (not HR): every onboarding he creates needs one of `departments`. */
  requireDepartments: boolean;
  canCreateOnboarding: boolean;
}

export interface DepartmentsPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

export interface DepartmentsPageResult {
  collection: Department[];
  pagination: DepartmentsPagination | null;
}

/** Unpaged: returns ALL top-level departments (children nested in subgroups). */
export const fetchDepartments = async (companyId: string): Promise<Department[]> => {
  const url = `${API_URL}/${companyId}/departments`;
  return axios.get(url).then((response) => response.data);
};

/** Paged: returns `{ collection, pagination }` for the infinite-scroll list. */
export const fetchDepartmentsPaged = async (
  companyId: string,
  filtersValue: { textSearch?: string } = {},
  opts: { page?: number; pageSize?: number } = {}
): Promise<DepartmentsPageResult> => {
  const params = new URLSearchParams();
  params.set("page", String(opts.page || 1));
  params.set("limit", String(opts.pageSize || 20));
  const search = (filtersValue.textSearch || "").trim();
  if (search) {
    params.set("search", search);
  }
  const url = `${API_URL}/${companyId}/departments?${params.toString()}`;
  return axios.get(url).then((response) => response.data);
};

export const fetchDepartmentById = async (companyId: string, id: string) => {
  const url = `${API_URL}/${companyId}/departments/${id}`;
  return axios.get(url).then((response) => response.data);
};

/** Single hydrated row (head + members + memberCount + subgroups). */
export const fetchDepartmentRow = async (
  companyId: string,
  id: string
): Promise<Department> => {
  const url = `${API_URL}/${companyId}/departments/${id}/row`;
  return axios.get(url).then((response) => response.data);
};

export const fetchDepartmentCompanyUsers = async (
  companyId: string
): Promise<DepartmentMember[]> => {
  const url = `${API_URL}/${companyId}/departments/company-users`;
  return axios.get(url).then((response) => response.data);
};

export const createDepartment = async (
  formData: DepartmentFormData,
  companyId: string
) => {
  const url = `${API_URL}/${companyId}/departments`;
  return axios.post(url, formData).then((response) => response.data);
};

export const updateDepartment = async (
  formData: DepartmentFormData,
  companyId: string
) => {
  const url = `${API_URL}/${companyId}/departments/${formData.id}`;
  return axios.patch(url, formData).then((response) => response.data);
};

export const fetchAssignableDepartments = async (
  companyId: string
): Promise<AssignableDepartmentsResponse> => {
  const url = `${API_URL}/${companyId}/departments/assignable`;
  return axios.get(url).then((response) => response.data);
};
